import { useTranslation } from "react-i18next";
import {useState, useEffect, useContext} from "react";
import {useDispatch, useSelector} from "react-redux";
import { useGetEDIVersionsQuery, useGetTriggerEventsQuery } from '../../../redux/api';
import DelimiterInput from "../DelimiterInput";
import {treeActions} from "../../../redux/slices/treeSlice";
import GroupByName from "../GroupByName";
import { each } from "lodash";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, styled, tooltipClasses } from "@mui/material";
import TPCheckbox from '../TPCheckbox';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import OverrideInboundDelimeter from "../OverrideInboundDelimeter";
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import  LightTooltip  from "../../../commonComponents/LightTooltip";

export default function Hl7Form() {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const dispatch = useDispatch();
  const { account } = useSelector(state => state.queryParams);
  const [version, setVersion] = useState('');
  const [versionLoading, setVersionLoading] = useState(true);
  const [versionOptions, setVersionOptions] = useState([{key: '', text: '', value: ''}]);
  const [versionWarningIcon, setVersionWarningIcon] = useState(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>);
  const [messageType, setMessageType] = useState('');
  const [messageTypeLoading, setMessageTypeLoading] = useState(true);
  const [messageTypeOptions, setMessageTypeOptions] = useState([]);
  const [messageWarningIcon, setMessageWarningIcon] = useState(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>);
  const [triggerEvent, setTriggerEvent] = useState('');
  const [triggerEventLoading, setTriggerEventLoading] = useState(true);
  const [triggerEventOptions, setTriggerEventOptions] = useState([]);
  const [recordDelimiter, setRecordDelimiter] = useState(false);
  const [fieldDelimiter, setFieldDelimiter] = useState(false);
  const [componentSeparator, setComponentSeparator] = useState(false);
  const [repetitionSeparator, setRepetitionSeparator] = useState(false);
  const [escapeCharacter, setEscapeCharactor] = useState(false);
  const [subcomponentSeparator, setSubcomponentSeparator] = useState(false);
  const [stripQuotes, setStripQuotes] = useState(false);
  const [trimData, setTrimData] = useState(false);
  const [decodeData, setDecodeData] = useState(false);
  const [unescapeData, setUnescapeData] = useState(false);
  const hl7VersionsRes = useGetEDIVersionsQuery({ data_type: 'HL7', account: account });
  const triggerEventsRes = useGetTriggerEventsQuery(
    { data_type_version: version, message_type: messageType, account: account },
    { skip: !version || !messageType } // Skip query if version or messageType is not available
  );
  

  const handleChange = (key, value) => {
    dispatch(treeActions.setRightTreeTemplateHeaders({ screen: parentScreen, key, value }));
  }

  // Function to set HL7 defaults 
  const setHL7DefaultsIfEmpty = () => {
    const header = rightTree.tree.templateHeader;
    
    // Get current delimiter values 
    const recordDelim = header.LINEDELIMITER || '';
    const fieldDelim = header.DELIMITER || '';
    const gdelim = header.GROUP_SEPARATOR || '';
    const sgdelim = header.SUBCOMPONENT_DELIMITER || '';
    const rdelim = header.REPETITION_DELIMITER || '';
    const edelim = header.ESCAPE_CHARACTER || '';
    
    if (sgdelim.length === 0 && rdelim.length === 0 && gdelim.length === 0) {
      // Create HL7 global substitutions 
      const hl7Substitutions = [
        { valueToReplace: '\\', replaceValue: '\\E\\', isRegularExpression: false },
        { valueToReplace: '\\r\\n', replaceValue: '\\.br\\', isRegularExpression: false },
        { valueToReplace: '\\r', replaceValue: '\\.br\\', isRegularExpression: false },
        { valueToReplace: '\\n', replaceValue: '\\.br\\', isRegularExpression: false },
        { valueToReplace: '|', replaceValue: '\\F\\', isRegularExpression: false },
        { valueToReplace: '&', replaceValue: '\\T\\', isRegularExpression: false },
        { valueToReplace: '^', replaceValue: '\\S\\', isRegularExpression: false },
        { valueToReplace: '~', replaceValue: '\\R\\', isRegularExpression: false }
      ];
      
      // Convert to GLOBAL_REPLACE string 
      const getGlobalSubstitutionString = (values = []) => {
        let globalString = "";
        values?.forEach(e => {
          const subString = `${e?.isRegularExpression ? "%1D" : ""}${encodeURIComponent(e?.valueToReplace)}%1F${encodeURIComponent(e?.replaceValue)}%1E`
          globalString += subString;
        })
        return globalString;
      };
      
      const HL7_DEFAULTS = {
        DELIMITER: '|',
        GROUP_SEPARATOR: '^',
        SUBCOMPONENT_DELIMITER: '&',
        REPETITION_DELIMITER: '~',
        LINEDELIMITER: 'CR',
        ESCAPE_CHARACTER: '\\',
        GLOBAL_REPLACE: getGlobalSubstitutionString(hl7Substitutions)
      };
      
      // Set all defaults
      Object.entries(HL7_DEFAULTS).forEach(([key, value]) => {
        handleChange(key, value);
      });
    }
  };

  useEffect(() => {
    const header = rightTree.tree.templateHeader;
    
    // Set HL7 defaults if this is HL7 and fields are empty
    if (header.FILETYPE === 'HL7') {
      setHL7DefaultsIfEmpty();
    }
    
    setMessageType(header.MESSAGE_TYPE || '');
    setTriggerEvent(header.TRIGGER_EVENT?.trim() || '');
    setRecordDelimiter(header.LINEDELIMITER || '');
    setFieldDelimiter(header.DELIMITER || '');
    setComponentSeparator(header.GROUP_SEPARATOR || '');
    setRepetitionSeparator(header.REPETITION_DELIMITER || '');
    setEscapeCharactor(header.ESCAPE_CHARACTER || '');
    setSubcomponentSeparator(header.SUBCOMPONENT_DELIMITER || '');
    setStripQuotes(
      header.UNCSV ?
        (typeof header.UNCSV === 'boolean' && header.UNCSV) || header.UNCSV === "TRUE" || false
        : false
    );
    setTrimData(
      header.TRIMDATA ?
        (typeof header.TRIMDATA === 'boolean' && header.TRIMDATA) || header.TRIMDATA === "TRUE" || false
        : false
    );
    setDecodeData(
      header.URLDECODE ?
        (typeof header.URLDECODE === 'boolean' && header.URLDECODE) || header.URLDECODE === "TRUE" || false
        : false
    );
    setUnescapeData(
      header.UNESCAPE_DATA ?
        (typeof header.UNESCAPE_DATA === 'boolean' && header.UNESCAPE_DATA) || header.UNESCAPE_DATA === "TRUE" || false
        : false
    );
  }, [rightTree.tree.templateHeader])

  useEffect(() => {
    if (!hl7VersionsRes.isSuccess) {
      setVersionLoading(true); 
      setMessageTypeLoading(true);
      return;
    }
    
    let header = rightTree.tree.templateHeader;
    let verObj = hl7VersionsRes.data.DATA_TYPE_VERSIONS;
    let ver = rightTree.tree.templateHeader.FILETYPE_VERSION;
    let msgArr = hl7VersionsRes.data.MESSAGE_TYPE;
    let msg = rightTree.tree.templateHeader.MESSAGE_TYPE;
    let versions = [];

    // DATA_TYPE_VERSIONS is sent as an object instead of array. 
    // Using lodash 'each' to loop the object and push to array to be used by dropdown.
    each(verObj, (val, k) => versions.push({ key: k, text: val, value: k }))
    setVersionOptions(versions);
    if (version !== header.FILETYPE_VERSION) setVersion(header.FILETYPE_VERSION);
    setMessageTypeOptions(msgArr.map((e, k) => {return { key: k + e, text: e, value: e }}));

    if (!ver || !versions.filter((o) => ver === o.key).length) {
      setVersion(versions[0].value);
      handleChange('FILETYPE_VERSION', versions[0].value);
    }
  
    if (!msg || !msgArr.filter((o) => msg === o).length) {
      setMessageType(msgArr[0]);
      handleChange('MESSAGE_TYPE', msgArr[0]);
    }
      
    setVersionLoading(false);
    setMessageTypeLoading(false);
  }, [hl7VersionsRes])

  useEffect(() => {
    if (!triggerEventsRes.isSuccess) return;

    let tgr = rightTree.tree.templateHeader.TRIGGER_EVENT?.trim();
    let tgrArr = triggerEventsRes.data.TRIGGER_EVENT;
    let tgrVal = tgrArr.length > 0 ? (tgrArr.includes(tgr) ? tgr : tgrArr[0]) : '';

    setTriggerEventOptions(tgrArr.map((e, k) => {return { key: k + e, text: e, value: e }}))
    setTriggerEvent(tgrVal);
    handleChange('TRIGGER_EVENT', tgrVal);
    setTriggerEventLoading(false);
  }, [triggerEventsRes])



  return (
    <>
      <>
        {/* Version */}
        <div className="flex width-100">
          <FormControl size="small" variant="outlined" className="width-100"> 
            <InputLabel id="versionDropdownLabel">{t('LBL_DATATYPE_VERSION')}</InputLabel>
            <Select
              size="small"
              variant="outlined"
              className="width-100"
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 250
                  }
                }
              }}
              labelId="versionDropdownLabel"
              value={version}
              onChange={(e) => {
                setVersion(e.target.value);
                handleChange('FILETYPE_VERSION', e.target.value);
                setTriggerEventLoading(true);
              }}
              label={t('LBL_DATATYPE_VERSION')}
            >
              {versionOptions.map(o => 
                <MenuItem key={'ver-'+o.value} value={o.value}>{o.text}</MenuItem>
              )}
            </Select>
          </FormControl>
          {
            !versionWarningIcon ? '' :
            <LightTooltip title={t('BOX_VER_CHANGE_WARNING')} placement="right-start">
              <Button
                size='small'
                sx={{
                  width: '30px !important', 
                  padding: '4px 8px !important', 
                  minWidth: '30px !important'
                }}
                onClick={() => setVersionWarningIcon(false)}
                onMouseEnter={() => setVersionWarningIcon(<VisibilityOffIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
                onMouseLeave={() => setVersionWarningIcon(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
              >
                {versionWarningIcon || ''}
              </Button>
            </LightTooltip>
          }
        </div>

        {/* Message Type */}
        <div className="flex width-100">
          <FormControl size="small" variant="outlined" className="width-100">
            {!messageTypeLoading && (
              <Autocomplete
                className="width-100"
                options={messageTypeOptions}
                getOptionLabel={(option) => option.text}
                value={messageTypeOptions.find((option) => option.text === messageType) || null}
                onChange={(event, value) => {
                  handleChange('MESSAGE_TYPE', value ? value.text : '');
                  setMessageType(value ? value.text : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('MESSAGE_TYPE')}
                    value={messageType}
                    onChange={(event) => {
                      handleChange('MESSAGE_TYPE', event.target.value);
                      setMessageType(event.target.value);
                    }}
                    variant="outlined"
                    size="small"
                    className="tp-hl7-message-type"
                  />
                )}
              />
            )}
          </FormControl>
          {
            !messageWarningIcon ? '' :
            <LightTooltip title={t('BOX_MSGTYPE_TRGREVNT_CHANGE_WARNING')} placement="right-start">
              <Button 
                size='small'
                sx={{
                  width: '30px !important', 
                  padding: '4px 8px !important', 
                  minWidth: '30px !important'
                }}
                onClick={() => setMessageWarningIcon(false)}
                onMouseEnter={() => setMessageWarningIcon(<VisibilityOffIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
                onMouseLeave={() => setMessageWarningIcon(<ReportProblemRoundedIcon sx={{fontSize: '26px', color: '#c4c4c4'}}/>)}
              >
                {messageWarningIcon || ''}
              </Button>
            </LightTooltip>
          }
        </div> 
        
        {/* Trigger Event */}
        <div className="flex width-100">
          <FormControl size="small" variant="outlined" className="width-100">
            <Autocomplete
              disabled={triggerEventLoading}
              className="width-100"
              options={triggerEventOptions}
              getOptionLabel={(option) => option.text}
              value={triggerEventOptions.find((option) => option.text === triggerEvent) || null}
              onChange={(event, value) => {
                setTriggerEvent(value ? value.text : '');
                handleChange('TRIGGER_EVENT', value ? value.text : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('TRIGGER_EVENT')}
                  value={triggerEvent}
                  onChange={(event) => {
                    setTriggerEvent(event.target.value);
                    handleChange('TRIGGER_EVENT', event.target.value);
                  }}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </FormControl>
        </div> 
      </>

      <>
        <DelimiterInput
          default
          label={t('LBL_REC_DELIM')}
          value={recordDelimiter}
          name={'LINEDELIMITER'}
        />
        <DelimiterInput
          default
          label={t('LBL_FLD_DELIM')}
          value={fieldDelimiter}
          name={'DELIMITER'}
        />
        <DelimiterInput
          default
          label={t('LBL_COMP_DELIM')}
          value={componentSeparator}
          name={'GROUP_SEPARATOR'}
        />
        <DelimiterInput
          default
          label={t('LBL_REPEAT_DELIM')}
          value={repetitionSeparator}
          name={'REPETITION_DELIMITER'}
        />
        <DelimiterInput
          default
          label={t('LBL_ESCAPE_DELIM')}
          value={escapeCharacter}
          name={'ESCAPE_CHARACTER'}
        />
        <DelimiterInput
          default
          label={t('LBL_SUB_COMP_DELIM')}
          value={subcomponentSeparator}
          name={'SUBCOMPONENT_DELIMITER'}
        />
      </>
      
      {parentScreen === 'inbound' && <>
          <div className="flex flex-wrap">
            <TPCheckbox 
              label={t('BTN_STRIP_QUOTES')}
              checked={stripQuotes}
              handleCheck={(checked) => {
                setStripQuotes(checked);
                handleChange('UNCSV', checked);
              }}
            />
            <TPCheckbox 
              label={t('BTN_TRIM_DATA')}
              checked={trimData}
              handleCheck={(checked) => {
                setTrimData(checked);
                handleChange('TRIMDATA', checked);
              }}
            />
            <TPCheckbox 
              label={t('BTN_URLDECODE_DATA')}
              checked={decodeData}
              handleCheck={(checked) => {
                setDecodeData(checked);
                handleChange('URLDECODE', checked);
              }}
            />
            <TPCheckbox 
              label={t('BTN_UNESCAPE_DATA')}
              checked={unescapeData}
              handleCheck={(checked) => {
                setUnescapeData(checked);
                handleChange('UNESCAPE_DATA', checked);
              }}
            />
          </div>
          <GroupByName />
        </>
      }
      {parentScreen === 'outbound' && <OverrideInboundDelimeter/>}
    </>
  )
}

